import { useMemo, useState } from "react"
import { Box, useTheme } from "@mui/material"
import intl from "src/internationalization/intl"
import { Grid, Typography } from "src/lib"
import { niceTicks } from "src/lib/utils/chartMath"

interface PerformanceScatterPlotEntity {
    id: string
    label: string
    /** Valor no eixo horizontal, no domínio próprio de cada uso (ex.: performance %, valor de AuC, contagem). */
    x: number
    /** Valor no eixo vertical, idem. */
    y: number
}

interface PerformanceScatterPlotProps {
    title: string
    entities: PerformanceScatterPlotEntity[]
    xFormat: (value: number) => string
    yFormat: (value: number) => string
    xAxisLabel: string
    yAxisLabel: string
    /** Direção favorável de cada eixo (default "up" — maior é melhor, caso do X e Y de execução). */
    xFavorableDirection?: "up" | "down"
    yFavorableDirection?: "up" | "down"
    /** Medida de tendência central usada nas linhas divisórias de quadrante e no rótulo (default "mean"). */
    centralTendency?: "mean" | "median"
}

const median = (values: number[]): number => {
    if (values.length === 0) return 0
    const sorted = [...values].sort((a, b) => a - b)
    const mid = Math.floor(sorted.length / 2)
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]
}

const CHART_W = 480
const CHART_H = 280
const PADDING = { top: 20, right: 16, bottom: 46, left: 64 }

// A marca não usa amarelo em nenhum outro lugar (ver comentário em src/lib/styles/theme.ts sobre
// accentWarning), mas aqui é o único jeito de diferenciar "misto" de "bom"/"ruim" sem reintroduzir
// as 2 cores antigas — escopado só a este gráfico, não promovido ao tema compartilhado.
const MIXED_QUADRANT_COLOR = "#FFD479"

/**
 * Dispersão real (X = performance de execução, Y = volume/clientes executados):
 * cada eixo tem significado próprio, então nada de jitter artificial — quem quer
 * saber "quem executa mais E melhor" lê o quadrante superior-direito.
 */
const PerformanceScatterPlot = ({
    title,
    entities,
    xFormat,
    yFormat,
    xAxisLabel,
    yAxisLabel,
    xFavorableDirection = "up",
    yFavorableDirection = "up",
    centralTendency = "mean",
}: PerformanceScatterPlotProps) => {
    const theme = useTheme() as any
    const ringColor = theme.palette.neutral.darkest
    const [hoveredId, setHoveredId] = useState<string | null>(null)

    const { xTicks, xMin, xMax } = useMemo(() => {
        const values = entities.map((e) => e.x)
        const t = niceTicks(Math.min(0, ...values), Math.max(0, ...values), 4)
        return { xTicks: t, xMin: t[0], xMax: t[t.length - 1] }
    }, [entities])

    const { yTicks, yMin, yMax } = useMemo(() => {
        const values = entities.map((e) => e.y)
        const t = niceTicks(Math.min(0, ...values), Math.max(0, ...values), 4)
        return { yTicks: t, yMin: t[0], yMax: t[t.length - 1] }
    }, [entities])

    if (entities.length === 0) return null

    const plotW = CHART_W - PADDING.left - PADDING.right
    const plotH = CHART_H - PADDING.top - PADDING.bottom

    const meanX =
        centralTendency === "median"
            ? median(entities.map((e) => e.x))
            : entities.reduce((sum, e) => sum + e.x, 0) / entities.length
    const meanY =
        centralTendency === "median"
            ? median(entities.map((e) => e.y))
            : entities.reduce((sum, e) => sum + e.y, 0) / entities.length

    const xAt = (value: number) => PADDING.left + ((value - xMin) / (xMax - xMin || 1)) * plotW
    const yAt = (value: number) => PADDING.top + plotH - ((value - yMin) / (yMax - yMin || 1)) * plotH

    const hovered = entities.find((e) => e.id === hoveredId)
    const gridColor = "rgba(255, 255, 255, 0.08)"
    const meanColor = "rgba(255, 255, 255, 0.3)"
    const axisTextColor = "rgba(255, 255, 255, 0.45)"

    // Cor por quadrante (posição do assessor/oportunidade em relação às duas médias, respeitando
    // a direção favorável de cada eixo), não por identidade — favorável nos dois / desfavorável
    // nos dois / misto (favorável em só um).
    const quadrantColors = {
        favorable: theme.palette.accentSuccess.main,
        unfavorable: theme.palette.accentError.main,
        mixed: MIXED_QUADRANT_COLOR,
    }
    const colorFor = (e: PerformanceScatterPlotEntity) => {
        const xFavorable = xFavorableDirection === "up" ? e.x >= meanX : e.x <= meanX
        const yFavorable = yFavorableDirection === "up" ? e.y >= meanY : e.y <= meanY
        if (xFavorable && yFavorable) return quadrantColors.favorable
        if (!xFavorable && !yFavorable) return quadrantColors.unfavorable
        return quadrantColors.mixed
    }

    return (
        <Box>
            <Grid container justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5, flexWrap: "wrap", rowGap: 0.5 }}>
                <Typography variant="h5">{title}</Typography>
                <Typography variant="caption" color="quaternary">
                    {intl(centralTendency === "median" ? "executionMedianLabel" : "executionAverageLabel")}: {xFormat(meanX)} · {yFormat(meanY)}
                </Typography>
            </Grid>
            <Grid container gap={1.5} sx={{ mb: 1, flexWrap: "wrap" }}>
                {[
                    { color: quadrantColors.favorable, label: intl("scatterQuadrantFavorable") },
                    { color: quadrantColors.unfavorable, label: intl("scatterQuadrantUnfavorable") },
                    { color: quadrantColors.mixed, label: intl("scatterQuadrantMixed") },
                ].map((item) => (
                    <Grid key={item.label} container alignItems="center" gap={0.5} wrap="nowrap" sx={{ width: "auto" }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: item.color, flexShrink: 0 }} />
                        <Typography variant="caption" color="quaternary" noWrap>
                            {item.label}
                        </Typography>
                    </Grid>
                ))}
            </Grid>
            <Box sx={{ position: "relative", width: "100%" }}>
                <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} width="100%" height={CHART_H} preserveAspectRatio="xMinYMin meet" role="img" aria-label={`${title}: gráfico de dispersão`}>
                    {yTicks.map((t) => (
                        <g key={t}>
                            <line x1={PADDING.left} x2={CHART_W - PADDING.right} y1={yAt(t)} y2={yAt(t)} stroke={gridColor} strokeWidth={1} />
                            <text x={PADDING.left - 6} y={yAt(t) + 3} fontSize={10} fill={axisTextColor} textAnchor="end">
                                {yFormat(t)}
                            </text>
                        </g>
                    ))}
                    {xTicks.map((t) => (
                        <g key={t}>
                            <line x1={xAt(t)} x2={xAt(t)} y1={PADDING.top} y2={PADDING.top + plotH} stroke={gridColor} strokeWidth={1} />
                            <text x={xAt(t)} y={PADDING.top + plotH + 16} fontSize={10} fill={axisTextColor} textAnchor="middle">
                                {xFormat(t)}
                            </text>
                        </g>
                    ))}

                    <line x1={xAt(meanX)} x2={xAt(meanX)} y1={PADDING.top} y2={PADDING.top + plotH} stroke={meanColor} strokeWidth={1} strokeDasharray="4 3" />
                    <line x1={PADDING.left} x2={CHART_W - PADDING.right} y1={yAt(meanY)} y2={yAt(meanY)} stroke={meanColor} strokeWidth={1} strokeDasharray="4 3" />

                    {entities.map((e) => (
                        <g key={e.id} style={{ cursor: "pointer" }}>
                            <circle
                                cx={xAt(e.x)}
                                cy={yAt(e.y)}
                                r={12}
                                fill="transparent"
                                tabIndex={0}
                                role="button"
                                aria-label={`${e.label}: ${xFormat(e.x)}, ${yFormat(e.y)}`}
                                onMouseEnter={() => setHoveredId(e.id)}
                                onMouseLeave={() => setHoveredId((id) => (id === e.id ? null : id))}
                                onFocus={() => setHoveredId(e.id)}
                                onBlur={() => setHoveredId((id) => (id === e.id ? null : id))}
                            />
                            <circle
                                cx={xAt(e.x)}
                                cy={yAt(e.y)}
                                r={hoveredId === e.id ? 6.5 : 5}
                                fill={colorFor(e)}
                                stroke={ringColor}
                                strokeWidth={2}
                                style={{ pointerEvents: "none" }}
                            />
                        </g>
                    ))}
                </svg>
                {hovered && (
                    <Box
                        sx={{
                            position: "absolute",
                            left: `${(xAt(hovered.x) / CHART_W) * 100}%`,
                            top: `${((yAt(hovered.y) - 14) / CHART_H) * 100}%`,
                            transform: "translate(-50%, -100%)",
                            bgcolor: "neutral.darker",
                            border: "1px solid rgba(255, 255, 255, 0.08)",
                            borderRadius: 1,
                            px: 1.25,
                            py: 0.5,
                            pointerEvents: "none",
                            whiteSpace: "nowrap",
                            zIndex: 1,
                        }}
                    >
                        <Typography variant="caption" color="quaternary" sx={{ display: "block" }}>
                            {hovered.label}
                        </Typography>
                        <Typography sx={{ fontWeight: 700, color: "neutral.lighter", fontSize: 13 }}>
                            {xFormat(hovered.x)} · {yFormat(hovered.y)}
                        </Typography>
                    </Box>
                )}
            </Box>
            <Typography variant="caption" color="quaternary" sx={{ display: "block", textAlign: "center", mt: 0.5 }}>
                {intl("scatterAxisLabel", { values: { xAxisLabel, yAxisLabel } })}
            </Typography>
        </Box>
    )
}

export { PerformanceScatterPlot }
export type { PerformanceScatterPlotEntity }
